/**
 * ReportsModulePage.js - CG Business ERP
 * FASE 4: Sistema completo de relatórios com todos os botões obrigatórios
 * 
 * Botões: Gerar, Imprimir, Exportar, Guardar, Descarregar, Partilhar, Agendar, Copiar, Reset, Preset
 * Formatos: PDF, XLSX, CSV, TXT, JSON
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  FileText, Download, Printer, Save, Share2, Clock, Copy, RotateCcw,
  Filter, Settings, BarChart3, ShoppingCart, Package, Users, 
  DollarSign, Wrench, FileSpreadsheet, FileJson, File,
  Calendar, MapPin, ChevronDown, Loader2, Eye, Archive,
  AlertTriangle, Building2, Bookmark, Play, RefreshCw, Pencil, Power
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Definição dos relatórios disponíveis
const AVAILABLE_REPORTS = {
  vendas: [
    { id: 'sales-summary', name: 'Resumo de Vendas', description: 'Por período, loja, vendedor', icon: ShoppingCart, endpoint: 'sales/summary' },
    { id: 'sales-by-product', name: 'Vendas por Produto', description: 'Quantidade, receita, lucro, margem', icon: Package, endpoint: 'sales/by-product' },
    { id: 'sales-by-client', name: 'Vendas por Cliente', description: 'Individual e Empresa', icon: Users, endpoint: 'sales/by-client' },
    { id: 'sales-by-payment', name: 'Por Método de Pagamento', description: 'Cash, M-Pesa, cartão, etc.', icon: DollarSign, endpoint: 'sales/by-payment-method' },
  ],
  inventario: [
    { id: 'inventory-stock', name: 'Stock Actual', description: 'Por loja, categoria', icon: Package, endpoint: 'inventory/stock-current' },
    { id: 'inventory-movements', name: 'Movimentos de Stock', description: 'Entradas, saídas, transferências', icon: BarChart3, endpoint: 'inventory/movements' },
    { id: 'inventory-aging', name: 'Stock Parado (Aging)', description: 'Items sem movimento', icon: Clock, endpoint: 'inventory/aging' },
  ],
  compras: [
    { id: 'purchases-summary', name: 'Resumo de Compras', description: 'Por fornecedor, produto', icon: ShoppingCart, endpoint: 'purchases/summary' },
  ],
  servicos: [
    { id: 'services-summary', name: 'Resumo de Serviços', description: 'Entradas, entregas, tempo médio', icon: Wrench, endpoint: 'services/summary' },
  ],
  financeiro: [
    { id: 'cash-summary', name: 'Resumo de Caixa', description: 'Recebimentos, pagamentos, saldo', icon: DollarSign, endpoint: 'financial/cash-summary' },
    { id: 'informal-exposure', name: 'Exposição Informal', description: 'Compras sem documento e risco', icon: AlertTriangle, endpoint: 'informal-procurement/exposure-report', isExternal: true },
  ],
};

// Formatos de exportação
const EXPORT_FORMATS = [
  { id: 'pdf', name: 'PDF', icon: FileText, color: 'text-red-500' },
  { id: 'xlsx', name: 'Excel (XLSX)', icon: FileSpreadsheet, color: 'text-green-500' },
  { id: 'csv', name: 'CSV', icon: File, color: 'text-blue-500' },
  { id: 'txt', name: 'TXT', icon: File, color: 'text-gray-500' },
  { id: 'json', name: 'JSON', icon: FileJson, color: 'text-yellow-500' },
];

// Períodos predefinidos
const PERIOD_PRESETS = [
  { id: 'today', name: 'Hoje' },
  { id: 'yesterday', name: 'Ontem' },
  { id: 'this_week', name: 'Esta Semana' },
  { id: 'this_month', name: 'Este Mês' },
  { id: 'last_30_days', name: 'Últimos 30 Dias' },
  { id: 'last_90_days', name: 'Últimos 90 Dias' },
  { id: 'this_year', name: 'Este Ano' },
  { id: 'custom', name: 'Personalizado' },
];

const SCHEDULE_REPORT_TYPES = [
  { id: 'sales_cash_summary', name: 'Vendas + Caixa (Resumo Diário)' },
  { id: 'stock_summary', name: 'Stock (ruptura + stale + capital)' },
  { id: 'ar_summary', name: 'Contas a Receber (AR)' },
  { id: 'margin_summary', name: 'Margens (confidence + warnings)' },
];

const SCHEDULE_FREQUENCIES = [
  { id: 'daily', name: 'Diário' },
  { id: 'weekly', name: 'Semanal' },
  { id: 'monthly', name: 'Mensal' },
];

const DELIVERY_MODES = [
  { id: 'archive_only', name: 'Apenas Arquivo' },
  { id: 'email_when_enabled', name: 'Email quando SMTP habilitado' },
];

export default function ReportsModulePage({ embedded = false }) {
  const { token, user, getToken } = useAuth();
  const [activeCategory, setActiveCategory] = useState('vendas');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Get token with fallback
  const authToken = token || getToken();
  
  // Filtros
  const [filters, setFilters] = useState({
    period_preset: 'this_month',
    date_from: '',
    date_to: '',
    location_id: '',
    category: '',
    supplier_id: '',
    client_type: '',
  });
  
  // Dados auxiliares
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [archives, setArchives] = useState([]);
  const [archiveConfidenceFilter, setArchiveConfidenceFilter] = useState('all');
  const [schedules, setSchedules] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  
  // Modals
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [presetName, setPresetName] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    is_active: true,
    report_type: 'sales_cash_summary',
    format: 'pdf',
    frequency: 'daily',
    byhour: 18,
    byminute: 0,
    day_of_week: 0,
    day_of_month: 1,
    location_id: '',
    delivery_mode: 'archive_only',
    recipients_text: '',
    period_preset: 'today',
  });

  const filteredArchives = archives.filter((archive) => {
    if (archiveConfidenceFilter === 'all') return true;
    return (archive.report_confidence || 'ok') === archiveConfidenceFilter;
  });

  const fetchSchedules = useCallback(async () => {
    if (!authToken) return;
    try {
      setSchedulesLoading(true);
      const response = await fetch(`${API_URL}/api/report-schedules/`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) throw new Error('Erro ao listar agendamentos');
      const data = await response.json();
      setSchedules(data.items || []);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar agendamentos');
    } finally {
      setSchedulesLoading(false);
    }
  }, [authToken]);

  const fetchArchive = useCallback(async () => {
    if (!authToken) return;
    try {
      setArchivesLoading(true);
      const response = await fetch(`${API_URL}/api/report-archive/?limit=50`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) throw new Error('Erro ao listar arquivo');
      const data = await response.json();
      setArchives(data.items || []);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar arquivo de relatórios');
    } finally {
      setArchivesLoading(false);
    }
  }, [authToken]);

  // Carregar dados auxiliares
  useEffect(() => {
    const fetchAuxData = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${authToken}` };

        const [locRes, catRes] = await Promise.all([
          fetch(`${API_URL}/api/settings/locations`, { headers }),
          fetch(`${API_URL}/api/catalogs/product-categories`, { headers }),
        ]);

        if (locRes.ok) {
          const locData = await locRes.json();
          setLocations(locData.locations || locData || []);
        }
        if (catRes.ok) {
          const catData = await catRes.json();
          setCategories(catData.categories || catData || []);
        }

        await Promise.all([fetchSchedules(), fetchArchive()]);
      } catch (error) {
        console.error('Erro ao carregar dados auxiliares:', error);
      }
    };

    if (authToken) fetchAuxData();
  }, [authToken, fetchSchedules, fetchArchive]);

  // Gerar relatório
  const generateReport = useCallback(async () => {
    if (!selectedReport) {
      toast.error('Seleccione um relatório');
      return;
    }
    
    setLoading(true);
    setReportData(null);
    
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const endpoint = selectedReport.isExternal 
        ? selectedReport.endpoint 
        : `reports/${selectedReport.endpoint}`;
      
      const response = await fetch(
        `${API_URL}/api/${endpoint}?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      
      if (!response.ok) throw new Error('Erro ao gerar relatório');
      
      const data = await response.json();
      setReportData(data);
      toast.success('Relatório gerado com sucesso');
    } catch (error) {
      toast.error('Erro ao gerar relatório');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedReport, filters, authToken]);

  // Exportar relatório
  const exportReport = async (format) => {
    if (!selectedReport) {
      toast.error('Seleccione um relatório primeiro');
      return;
    }
    
    setExporting(true);
    setShowExportMenu(false);
    
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await fetch(
        `${API_URL}/api/reports/${selectedReport.id}/export?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      
      if (!response.ok) throw new Error('Erro ao exportar');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedReport.id}_${new Date().toISOString().slice(0,10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Relatório exportado em ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Erro ao exportar relatório');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  // Imprimir relatório
  const printReport = async () => {
    if (!selectedReport) {
      toast.error('Seleccione um relatório primeiro');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const url = `${API_URL}/api/reports/${selectedReport.id}/print?${params.toString()}`;
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          setTimeout(() => printWindow.print(), 500);
        });
      }
    } catch (error) {
      toast.error('Erro ao preparar impressão');
    }
  };

  // Copiar para clipboard
  const copyToClipboard = () => {
    if (!reportData?.data) {
      toast.error('Gere o relatório primeiro');
      return;
    }
    
    const data = reportData.data;
    if (!data.length) {
      toast.error('Sem dados para copiar');
      return;
    }
    
    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(h => row[h] ?? '').join('\t'));
    const text = [headers.join('\t'), ...rows].join('\n');
    
    navigator.clipboard.writeText(text);
    toast.success('Dados copiados para a área de transferência (formato TSV)');
  };

  // Guardar preset
  const savePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Nome do preset obrigatório');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/reports/presets?report_name=${selectedReport?.id || 'global'}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: presetName,
            filters: filters,
            is_default: false
          })
        }
      );
      
      if (!response.ok) throw new Error();
      
      toast.success('Preset guardado');
      setShowPresetDialog(false);
      setPresetName('');
    } catch {
      toast.error('Erro ao guardar preset');
    }
  };

  // Reset filtros
  const resetFilters = () => {
    setFilters({
      period_preset: 'this_month',
      date_from: '',
      date_to: '',
      location_id: '',
      category: '',
      supplier_id: '',
      client_type: '',
    });
    setReportData(null);
    toast.info('Filtros resetados');
  };

  // Guardar no arquivo
  const saveToArchive = async () => {
    if (!selectedReport || !reportData) {
      toast.error('Gere um relatório primeiro');
      return;
    }
    
    try {
      // Export com save_to_archive=true
      const params = new URLSearchParams();
      params.append('format', 'json');
      params.append('save_to_archive', 'true');
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await fetch(
        `${API_URL}/api/reports/${selectedReport.id}/export?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      
      if (!response.ok) throw new Error();
      
      toast.success('Relatório guardado no arquivo');
      
      await fetchArchive();
    } catch {
      toast.error('Erro ao guardar no arquivo');
    }
  };

  const openCreateSchedule = () => {
    setEditingScheduleId(null);
    setScheduleForm({
      name: '',
      is_active: true,
      report_type: 'sales_cash_summary',
      format: 'pdf',
      frequency: 'daily',
      byhour: 18,
      byminute: 0,
      day_of_week: 0,
      day_of_month: 1,
      location_id: '',
      delivery_mode: 'archive_only',
      recipients_text: '',
      period_preset: 'today',
    });
    setShowScheduleDialog(true);
  };

  const openEditSchedule = (schedule) => {
    setEditingScheduleId(schedule.id);
    setScheduleForm({
      name: schedule.name || '',
      is_active: schedule.is_active !== false,
      report_type: schedule.report_type || 'sales_cash_summary',
      format: schedule.format || 'pdf',
      frequency: schedule.frequency || 'daily',
      byhour: Number(schedule.byhour ?? 18),
      byminute: Number(schedule.byminute ?? 0),
      day_of_week: Number(schedule.day_of_week ?? 0),
      day_of_month: Number(schedule.day_of_month ?? 1),
      location_id: schedule.location_id || '',
      delivery_mode: schedule.delivery_mode || 'archive_only',
      recipients_text: (schedule.recipients || []).join(', '),
      period_preset: schedule?.filters?.period_preset || 'today',
    });
    setShowScheduleDialog(true);
  };

  const saveSchedule = async () => {
    if (!scheduleForm.name.trim()) {
      toast.error('Nome do agendamento é obrigatório');
      return;
    }

    const recipients = scheduleForm.recipients_text
      .split(',')
      .map(r => r.trim())
      .filter(Boolean);

    const payload = {
      name: scheduleForm.name.trim(),
      is_active: scheduleForm.is_active,
      report_type: scheduleForm.report_type,
      format: scheduleForm.format,
      frequency: scheduleForm.frequency,
      byhour: Number(scheduleForm.byhour),
      byminute: Number(scheduleForm.byminute),
      day_of_week: Number(scheduleForm.day_of_week),
      day_of_month: Number(scheduleForm.day_of_month),
      location_id: scheduleForm.location_id || null,
      delivery_mode: scheduleForm.delivery_mode,
      recipients,
      filters: {
        period_preset: scheduleForm.period_preset,
        location_id: scheduleForm.location_id || undefined,
      },
    };

    try {
      setSavingSchedule(true);
      const method = editingScheduleId ? 'PUT' : 'POST';
      const url = editingScheduleId
        ? `${API_URL}/api/report-schedules/${editingScheduleId}`
        : `${API_URL}/api/report-schedules/`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erro ao guardar agendamento');
      }

      toast.success(editingScheduleId ? 'Agendamento actualizado' : 'Agendamento criado');
      setShowScheduleDialog(false);
      await fetchSchedules();
    } catch (error) {
      toast.error(error.message || 'Erro ao guardar agendamento');
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleScheduleActive = async (schedule) => {
    try {
      const response = await fetch(`${API_URL}/api/report-schedules/${schedule.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !schedule.is_active }),
      });

      if (!response.ok) throw new Error('Falha ao alterar estado');
      toast.success(!schedule.is_active ? 'Agendamento activado' : 'Agendamento desactivado');
      await fetchSchedules();
    } catch (error) {
      toast.error(error.message || 'Erro ao alterar estado');
    }
  };

  const runScheduleNow = async (schedule) => {
    try {
      const response = await fetch(`${API_URL}/api/report-schedules/${schedule.id}/run-now`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erro ao executar agendamento');
      }

      const data = await response.json();
      toast.success('Execução concluída', {
        description: `Arquivo: ${data.archive_id} | entrega: ${data.delivery_status}`
      });
      await Promise.all([fetchSchedules(), fetchArchive()]);
    } catch (error) {
      toast.error(error.message || 'Erro ao executar agendamento');
    }
  };

  const downloadArchiveFile = async (archive) => {
    try {
      const response = await fetch(`${API_URL}/api/report-archive/${archive.id}/download`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Falha no download');
      }

      const blob = await response.blob();
      const fileName = archive.file_name || `${archive.report_type}_${archive.id}.${archive.format}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Download iniciado');
    } catch (error) {
      toast.error(error.message || 'Erro ao descarregar arquivo');
    }
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'} data-testid="reports-module-page">
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              Módulo de Relatórios
            </h1>
            <p className="text-muted-foreground">Gere, exporte, imprima e arquive relatórios do ERP</p>
          </div>
        </div>
      )}
        
      <div className="flex items-center justify-between flex-wrap gap-4">
        {embedded ? (
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Relatórios operacionais, presets e arquivo
            </h2>
            <p className="text-sm text-muted-foreground">Gere, exporte, imprima, arquive e agende relatórios no mesmo espaço.</p>
          </div>
        ) : <div />}

        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            onClick={generateReport} 
            disabled={!selectedReport || loading}
            data-testid="btn-gerar"
            className="bg-primary"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Gerar
          </Button>
          
          <Button variant="outline" onClick={printReport} disabled={!selectedReport} data-testid="btn-imprimir">
            <Printer className="h-4 w-4 mr-2" />
            Imprimir
          </Button>
          
          <div className="relative">
            <Button 
              variant="outline" 
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={!selectedReport || exporting}
              data-testid="btn-exportar"
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Exportar
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-background border rounded-md shadow-lg z-50">
                {EXPORT_FORMATS.map(format => (
                  <button
                    key={format.id}
                    onClick={() => exportReport(format.id)}
                    className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2"
                    data-testid={`export-${format.id}`}
                  >
                    <format.icon className={`h-4 w-4 ${format.color}`} />
                    {format.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <Button variant="outline" onClick={saveToArchive} disabled={!reportData} data-testid="btn-guardar">
            <Save className="h-4 w-4 mr-2" />
            Guardar
          </Button>
          
          <Button variant="outline" onClick={() => exportReport('pdf')} disabled={!selectedReport} data-testid="btn-descarregar">
            <Download className="h-4 w-4 mr-2" />
            Descarregar
          </Button>
          
          <Button variant="outline" onClick={() => toast.info('Partilhar: Em desenvolvimento')} data-testid="btn-partilhar">
            <Share2 className="h-4 w-4 mr-2" />
            Partilhar
          </Button>
          
          <Button variant="outline" onClick={openCreateSchedule} data-testid="btn-agendar">
            <Clock className="h-4 w-4 mr-2" />
            Agendar
          </Button>
          
          <Button variant="outline" onClick={copyToClipboard} disabled={!reportData} data-testid="btn-copiar">
            <Copy className="h-4 w-4 mr-2" />
            Copiar
          </Button>
          
          <Button variant="outline" onClick={resetFilters} data-testid="btn-reset">
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          
          <Button variant="outline" onClick={() => setShowPresetDialog(true)} data-testid="btn-preset">
            <Bookmark className="h-4 w-4 mr-2" />
            Preset
          </Button>
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar - Categorias e Relatórios */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Categorias</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {Object.keys(AVAILABLE_REPORTS).map(cat => (
                <button
                  key={cat}
                  onClick={() => {
                    setActiveCategory(cat);
                    setSelectedReport(null);
                    setReportData(null);
                  }}
                  className={`w-full px-3 py-2 text-left rounded-md text-sm capitalize transition-colors ${
                    activeCategory === cat 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-muted'
                  }`}
                >
                  {cat === 'vendas' && <ShoppingCart className="h-4 w-4 inline mr-2" />}
                  {cat === 'inventario' && <Package className="h-4 w-4 inline mr-2" />}
                  {cat === 'compras' && <Building2 className="h-4 w-4 inline mr-2" />}
                  {cat === 'servicos' && <Wrench className="h-4 w-4 inline mr-2" />}
                  {cat === 'financeiro' && <DollarSign className="h-4 w-4 inline mr-2" />}
                  {cat}
                </button>
              ))}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Relatórios Disponíveis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {AVAILABLE_REPORTS[activeCategory]?.map(report => (
                <button
                  key={report.id}
                  onClick={() => {
                    setSelectedReport(report);
                    setReportData(null);
                  }}
                  className={`w-full px-3 py-2 text-left rounded-md transition-colors ${
                    selectedReport?.id === report.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-muted'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <report.icon className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-medium">{report.name}</div>
                      <div className="text-xs opacity-70">{report.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
          
          {/* Arquivo Rápido */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Archive className="h-4 w-4" />
                Arquivo Recente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {archives.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-2">Nenhum relatório arquivado</p>
              ) : (
                <div className="space-y-1">
                  {archives.slice(0, 5).map(arch => (
                    <div key={arch.id} className="text-xs p-2 bg-muted rounded flex justify-between items-center">
                      <span className="truncate">{arch.report_name}</span>
                      <Badge variant="outline" className="text-[10px]">{arch.format}</Badge>
                    </div>
                  ))}
                </div>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full mt-2"
                onClick={() => setShowArchiveDialog(true)}
              >
                Ver Todos
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="col-span-12 lg:col-span-9 space-y-4">
          {/* Filtros */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filtros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs">Período</Label>
                  <Select 
                    value={filters.period_preset} 
                    onValueChange={(v) => setFilters({...filters, period_preset: v})}
                  >
                    <SelectTrigger className="h-9">
                      <Calendar className="h-3 w-3 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIOD_PRESETS.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {filters.period_preset === 'custom' && (
                  <>
                    <div>
                      <Label className="text-xs">Data Início</Label>
                      <Input 
                        type="date" 
                        className="h-9"
                        value={filters.date_from}
                        onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Data Fim</Label>
                      <Input 
                        type="date"
                        className="h-9"
                        value={filters.date_to}
                        onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                      />
                    </div>
                  </>
                )}
                
                <div>
                  <Label className="text-xs">Localização</Label>
                  <Select 
                    value={filters.location_id || "all"} 
                    onValueChange={(v) => setFilters({...filters, location_id: v === "all" ? "" : v})}
                  >
                    <SelectTrigger className="h-9">
                      <MapPin className="h-3 w-3 mr-2" />
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Categoria Produto</Label>
                  <Select 
                    value={filters.category || "all"} 
                    onValueChange={(v) => setFilters({...filters, category: v === "all" ? "" : v})}
                  >
                    <SelectTrigger className="h-9">
                      <Package className="h-3 w-3 mr-2" />
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.base_prefix}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview do Relatório */}
          <Card className="min-h-[500px]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  {selectedReport ? selectedReport.name : 'Seleccione um relatório'}
                </CardTitle>
                {reportData && (
                  <Badge variant="secondary">
                    {reportData.row_count || reportData.data?.length || 0} registos
                  </Badge>
                )}
              </div>
              {selectedReport && (
                <CardDescription>{selectedReport.description}</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <span className="ml-3 text-muted-foreground">A gerar relatório...</span>
                </div>
              ) : !selectedReport ? (
                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                  <BarChart3 className="h-16 w-16 mb-4 opacity-30" />
                  <p className="text-lg">Seleccione um relatório no painel lateral</p>
                  <p className="text-sm">Depois clique em &quot;Gerar&quot; para visualizar</p>
                </div>
              ) : !reportData ? (
                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                  <FileText className="h-16 w-16 mb-4 opacity-30" />
                  <p className="text-lg">Relatório: {selectedReport.name}</p>
                  <p className="text-sm mb-4">Configure os filtros e clique em &quot;Gerar&quot;</p>
                  <Button onClick={generateReport}>
                    <Play className="h-4 w-4 mr-2" />
                    Gerar Relatório
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  {(reportData.summary || reportData.totals) && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(reportData.summary || reportData.totals).slice(0, 8).map(([key, value]) => (
                        <Card key={key} className="bg-muted/50">
                          <CardContent className="pt-3 pb-2">
                            <div className="text-xl font-bold">
                              {typeof value === 'number' 
                                ? (key.includes('value') || key.includes('revenue') || key.includes('cost') || key.includes('margin'))
                                  ? `${value.toLocaleString('pt-MZ')} MZN`
                                  : value.toLocaleString('pt-MZ')
                                : value}
                            </div>
                            <div className="text-xs text-muted-foreground capitalize">
                              {key.replace(/_/g, ' ')}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                  
                  {/* Data Table */}
                  {reportData.data && reportData.data.length > 0 && (
                    <div className="border rounded-md overflow-auto max-h-[350px]">
                      <table className="w-full text-sm">
                        <thead className="bg-muted sticky top-0 z-10">
                          <tr>
                            {Object.keys(reportData.data[0]).slice(0, 10).map(key => (
                              <th key={key} className="px-3 py-2 text-left font-medium capitalize text-xs">
                                {key.replace(/_/g, ' ')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.data.slice(0, 100).map((row, idx) => (
                            <tr key={idx} className="border-t hover:bg-muted/50">
                              {Object.entries(row).slice(0, 10).map(([key, val], i) => (
                                <td key={i} className="px-3 py-2 text-xs">
                                  {typeof val === 'number' 
                                    ? (key.includes('price') || key.includes('value') || key.includes('cost') || key.includes('revenue'))
                                      ? `${val.toLocaleString('pt-MZ')} MZN`
                                      : val.toLocaleString('pt-MZ')
                                    : String(val || '-').substring(0, 50)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Footer info */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                    <span>Gerado: {new Date(reportData.generated_at || Date.now()).toLocaleString('pt-MZ')}</span>
                    <span>Por: {reportData.generated_by || user?.full_name || 'Sistema'}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="scheduled-reports-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Relatórios Agendados
                  </CardTitle>
                  <CardDescription>Diário/Semanal/Mensal com execução manual e arquivo</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => Promise.all([fetchSchedules(), fetchArchive()])} data-testid="refresh-schedules-button">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Actualizar
                  </Button>
                  <Button size="sm" onClick={openCreateSchedule} data-testid="create-schedule-button">
                    <Clock className="h-4 w-4 mr-2" />
                    Criar Agendamento
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border rounded-md overflow-auto" data-testid="report-schedules-table">
                <table className="w-full text-sm min-w-[1100px]">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">Nome</th>
                      <th className="px-3 py-2 text-left">Tipo</th>
                      <th className="px-3 py-2 text-left">Formato</th>
                      <th className="px-3 py-2 text-left">Frequência</th>
                      <th className="px-3 py-2 text-left">Localização</th>
                      <th className="px-3 py-2 text-left">Último Run</th>
                      <th className="px-3 py-2 text-left">Próximo Run</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                      <th className="px-3 py-2 text-center">Acções</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedulesLoading ? (
                      <tr><td className="px-3 py-4" colSpan={9}>A carregar agendamentos...</td></tr>
                    ) : schedules.length === 0 ? (
                      <tr><td className="px-3 py-4 text-muted-foreground" colSpan={9}>Sem agendamentos configurados</td></tr>
                    ) : schedules.map((schedule) => (
                      <tr key={schedule.id} className="border-t" data-testid={`schedule-row-${schedule.id}`}>
                        <td className="px-3 py-2" data-testid={`schedule-name-${schedule.id}`}>{schedule.name}</td>
                        <td className="px-3 py-2" data-testid={`schedule-type-${schedule.id}`}>{schedule.report_type}</td>
                        <td className="px-3 py-2" data-testid={`schedule-format-${schedule.id}`}>{String(schedule.format || '').toUpperCase()}</td>
                        <td className="px-3 py-2" data-testid={`schedule-frequency-${schedule.id}`}>{schedule.frequency}</td>
                        <td className="px-3 py-2" data-testid={`schedule-location-${schedule.id}`}>{schedule.location_id || 'Global'}</td>
                        <td className="px-3 py-2" data-testid={`schedule-last-run-${schedule.id}`}>{schedule.last_run_at ? new Date(schedule.last_run_at).toLocaleString('pt-MZ') : '-'}</td>
                        <td className="px-3 py-2" data-testid={`schedule-next-run-${schedule.id}`}>{schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleString('pt-MZ') : '-'}</td>
                        <td className="px-3 py-2" data-testid={`schedule-active-${schedule.id}`}>
                          <Badge variant="outline" className={schedule.is_active ? 'bg-green-100 text-green-800 border-green-300' : 'bg-slate-100 text-slate-700'}>
                            {schedule.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => runScheduleNow(schedule)} data-testid={`run-now-schedule-${schedule.id}`}>
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openEditSchedule(schedule)} data-testid={`edit-schedule-${schedule.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => toggleScheduleActive(schedule)} data-testid={`toggle-schedule-${schedule.id}`}>
                              <Power className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border rounded-md overflow-auto" data-testid="scheduled-archive-table">
                <div className="p-3 border-b bg-slate-50 flex items-center gap-3" data-testid="archive-confidence-filter-wrapper">
                  <Label className="text-xs">Confiança</Label>
                  <Select value={archiveConfidenceFilter} onValueChange={setArchiveConfidenceFilter}>
                    <SelectTrigger className="w-44" data-testid="archive-confidence-filter-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="ok">ok</SelectItem>
                      <SelectItem value="partial">partial</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-muted-foreground" data-testid="archive-confidence-filter-count">
                    {filteredArchives.length} registos
                  </span>
                </div>
                <table className="w-full text-sm min-w-[960px]">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">Gerado em</th>
                      <th className="px-3 py-2 text-left">Tipo</th>
                      <th className="px-3 py-2 text-left">Formato</th>
                      <th className="px-3 py-2 text-left">Bytes</th>
                      <th className="px-3 py-2 text-left">Confiança</th>
                      <th className="px-3 py-2 text-left">Entrega</th>
                      <th className="px-3 py-2 text-left">Warnings</th>
                      <th className="px-3 py-2 text-center">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivesLoading ? (
                      <tr><td className="px-3 py-4" colSpan={8}>A carregar arquivo...</td></tr>
                    ) : filteredArchives.length === 0 ? (
                      <tr><td className="px-3 py-4 text-muted-foreground" colSpan={8}>Sem execuções arquivadas</td></tr>
                    ) : filteredArchives.slice(0, 20).map((archive) => (
                      <tr key={archive.id} className="border-t" data-testid={`archive-row-${archive.id}`}>
                        <td className="px-3 py-2" data-testid={`archive-generated-at-${archive.id}`}>{new Date(archive.generated_at).toLocaleString('pt-MZ')}</td>
                        <td className="px-3 py-2" data-testid={`archive-type-${archive.id}`}>{archive.report_type}</td>
                        <td className="px-3 py-2" data-testid={`archive-format-${archive.id}`}>{String(archive.format || '').toUpperCase()}</td>
                        <td className="px-3 py-2" data-testid={`archive-bytes-${archive.id}`}>{archive.file_bytes || 0}</td>
                        <td className="px-3 py-2" data-testid={`archive-confidence-${archive.id}`}>
                          <Badge variant="outline" className={archive.report_confidence === 'partial' ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-green-100 text-green-800 border-green-300'}>
                            {archive.report_confidence || 'ok'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2" data-testid={`archive-delivery-${archive.id}`}>{archive.delivery_status || '-'}</td>
                        <td className="px-3 py-2" data-testid={`archive-warnings-${archive.id}`}>{(archive.warnings || []).slice(0, 1).join(', ') || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          <Button size="sm" variant="outline" onClick={() => downloadArchiveFile(archive)} data-testid={`download-archive-${archive.id}`}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog: Guardar Preset */}
      <Dialog open={showPresetDialog} onOpenChange={setShowPresetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Guardar Preset de Filtros</DialogTitle>
            <DialogDescription>
              Guarde os filtros actuais para reutilização rápida
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome do Preset</Label>
              <Input 
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Ex: Vendas Mensais Maputo"
              />
            </div>
            <div className="bg-muted p-3 rounded-md text-xs">
              <p className="font-medium mb-1">Filtros actuais:</p>
              <p>Período: {filters.period_preset}</p>
              {filters.location_id && <p>Localização: {filters.location_id}</p>}
              {filters.category && <p>Categoria: {filters.category}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPresetDialog(false)}>Cancelar</Button>
            <Button onClick={savePreset}>
              <Save className="h-4 w-4 mr-2" />
              Guardar Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-2xl" data-testid="schedule-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingScheduleId ? 'Editar Agendamento' : 'Criar Agendamento'}</DialogTitle>
            <DialogDescription>
              Configure frequência, formato e entrega (arquivo/e-mail).
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Nome</Label>
              <Input value={scheduleForm.name} onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })} data-testid="schedule-name-input" />
            </div>

            <div>
              <Label>Tipo de Relatório</Label>
              <Select value={scheduleForm.report_type} onValueChange={(v) => setScheduleForm({ ...scheduleForm, report_type: v })}>
                <SelectTrigger data-testid="schedule-report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCHEDULE_REPORT_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Formato</Label>
              <Select value={scheduleForm.format} onValueChange={(v) => setScheduleForm({ ...scheduleForm, format: v })}>
                <SelectTrigger data-testid="schedule-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPORT_FORMATS.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Frequência</Label>
              <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm({ ...scheduleForm, frequency: v })}>
                <SelectTrigger data-testid="schedule-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCHEDULE_FREQUENCIES.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Período</Label>
              <Select value={scheduleForm.period_preset} onValueChange={(v) => setScheduleForm({ ...scheduleForm, period_preset: v })}>
                <SelectTrigger data-testid="schedule-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIOD_PRESETS.filter(p => p.id !== 'custom').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Hora</Label>
              <Input type="number" min={0} max={23} value={scheduleForm.byhour} onChange={(e) => setScheduleForm({ ...scheduleForm, byhour: e.target.value })} data-testid="schedule-hour-input" />
            </div>

            <div>
              <Label>Minuto</Label>
              <Input type="number" min={0} max={59} value={scheduleForm.byminute} onChange={(e) => setScheduleForm({ ...scheduleForm, byminute: e.target.value })} data-testid="schedule-minute-input" />
            </div>

            {scheduleForm.frequency === 'weekly' && (
              <div>
                <Label>Dia da Semana (0=Segunda)</Label>
                <Input type="number" min={0} max={6} value={scheduleForm.day_of_week} onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_week: e.target.value })} data-testid="schedule-day-of-week-input" />
              </div>
            )}

            {scheduleForm.frequency === 'monthly' && (
              <div>
                <Label>Dia do Mês</Label>
                <Input type="number" min={1} max={31} value={scheduleForm.day_of_month} onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_month: e.target.value })} data-testid="schedule-day-of-month-input" />
              </div>
            )}

            <div>
              <Label>Localização</Label>
              <Select value={scheduleForm.location_id || 'global'} onValueChange={(v) => setScheduleForm({ ...scheduleForm, location_id: v === 'global' ? '' : v })}>
                <SelectTrigger data-testid="schedule-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global</SelectItem>
                  {locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Entrega</Label>
              <Select value={scheduleForm.delivery_mode} onValueChange={(v) => setScheduleForm({ ...scheduleForm, delivery_mode: v })}>
                <SelectTrigger data-testid="schedule-delivery-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DELIVERY_MODES.map(mode => <SelectItem key={mode.id} value={mode.id}>{mode.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {scheduleForm.delivery_mode === 'email_when_enabled' && (
              <div className="col-span-2">
                <Label>Emails (separados por vírgula)</Label>
                <Input value={scheduleForm.recipients_text} onChange={(e) => setScheduleForm({ ...scheduleForm, recipients_text: e.target.value })} placeholder="financeiro@empresa.com, admin@empresa.com" data-testid="schedule-recipients-input" />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)} data-testid="cancel-schedule-button">Cancelar</Button>
            <Button onClick={saveSchedule} disabled={savingSchedule} data-testid="save-schedule-button">
              {savingSchedule ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Agendamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Arquivo de Relatórios */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Arquivo de Relatórios
            </DialogTitle>
            <DialogDescription>
              Relatórios guardados anteriormente para download
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-auto">
            {filteredArchives.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhum relatório arquivado</p>
            ) : (
              <div className="space-y-2">
                {filteredArchives.map(archive => (
                  <div key={archive.id} className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/50">
                    <div className="flex-1">
                      <div className="font-medium">{archive.schedule_name || archive.report_type || archive.report_name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3">
                        <span>{new Date(archive.generated_at).toLocaleString('pt-MZ')}</span>
                        <Badge variant="outline">{archive.format.toUpperCase()}</Badge>
                        <span>{archive.file_bytes || 0} bytes</span>
                        <span>{archive.generated_by_name || archive.generated_by_user_name || '-'}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => downloadArchiveFile(archive)} data-testid={`archive-dialog-download-${archive.id}`}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
